import { buildFrontendRecommendationOptions } from "@features/dashboard/recommendations";
import type { RecommendedSkillTask } from "@features/dashboard/recommendations/adapters/dashboard-task-types";
import { buildItemTypeSessionSection } from "@features/dashboard/recommendations/adapters/item-type-session-section";
import { useHydratedStudyRecommendations } from "@features/dashboard/recommendations/hooks/useHydratedStudyRecommendations";
import { ItemTypeDetailModal } from "@memorang/ui/components/ItemTypeDetailModal";
import { questionTypeIconMap, skillIconMap } from "@memorang/ui/constants";
import { useProficiencyIconMap } from "@memorang/ui/hooks/useProficiencyIconMap";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useItemTypeDetail } from "../hooks/use-item-type-detail";

type Props = {
	questionTypeId?: string;
	onClose: () => void;
	onStartRecommendedSession: (task: RecommendedSkillTask) => void;
	starting?: boolean;
};

const ItemTypeDetailModalContainer = ({
	questionTypeId,
	onClose,
	onStartRecommendedSession,
	starting,
}: Props) => {
	const { t } = useTranslation();
	const detail = useItemTypeDetail(questionTypeId);
	const numericQuestionTypeId = Number(questionTypeId);
	const hasQuestionTypeFilter = Number.isInteger(numericQuestionTypeId);
	const hydrated = useHydratedStudyRecommendations({
		durationOptions: true,
		filterValueIds: hasQuestionTypeFilter ? [numericQuestionTypeId] : [],
	});
	const proficiencyIconMap = useProficiencyIconMap();
	const recommendationOptions = useMemo(
		() =>
			hydrated.status === "READY" && hasQuestionTypeFilter
				? buildFrontendRecommendationOptions({
						result: hydrated.presentation,
						skills: hydrated.frontend.skills,
						proficiencies: hydrated.frontend.proficiencies,
						questionTypes: hydrated.frontend.questionTypes,
						translate: t,
						proficiencyIconMap,
						skillIconMap,
						questionTypeIconMap,
					})
				: [],
		[hydrated, hasQuestionTypeFilter, t, proficiencyIconMap],
	);
	const [selectedSessionId, setSelectedSessionId] = useState<string>();

	if (!detail) {
		return null;
	}

	const sessionSection = buildItemTypeSessionSection({
		options: recommendationOptions,
		selectedSessionId,
		sessionsLabel: t("dashboard.recommendedTasks.sectionTitle"),
		startLabel: t("sessionConfig.start"),
		starting,
		onSelectSession: setSelectedSessionId,
		onStart: (option) => {
			onClose();
			onStartRecommendedSession(option.task);
		},
	});

	return (
		<ItemTypeDetailModal
			visible
			onClose={onClose}
			{...detail}
			sessionSection={sessionSection}
		/>
	);
};

export default ItemTypeDetailModalContainer;
